import React, { useEffect, useState } from 'react';
import { fetchFraudAddresses, fetchFraudConfig, fetchOrderShipAddress, fetchShippingTypes } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Fraud Check: the score weights and the known fraud addresses the order scorer reads
 * (Azure exit WS8). Reads sql01.FraudConfiguration and FraudAddresses. Saving is off until
 * ws8_fraud_check_live. Not the Fraud Alert queue, which is a different screen.
 */

// The admin form, section by section. Shipping types come from ShippingMethods, keyed by name.
const SECTIONS = [
  ['Previous orders', [['moreThenTwo', 'More then 2'], ['onlyOneOrder', 'Only 1 order'], ['noOrder', 'No Order']]],
  ['Taken by', [['takenByOnline', 'Online'], ['takenByOther', 'Other']]],
  ['Order Total', [['orderAmountLessThen200', 'Order Amount less then $200'], ['orderAmountLessThen250', 'Order Amount less then $250'], ['orderAmountLessThen300', 'Order Amount less then $300'], ['orderAmountLessThen400', 'Order Amount less then $400'], ['orderAmountMoreThen400', 'Order Amount more then $400']]],
  ['Shipping Date', [['sameOrderDateShipDate', 'Order date and shipping date same'], ['orderDayFriday', 'Order day is friday']]],
  ['Null Email', [['nullEmailTrue', 'Value for True'], ['nullEmailFalse', 'Value for False']]],
  ['Free Email', [['freeEmails', 'List of Email Domains (e.g., gmail.com, aol.com)'], ['freeEmailTrue', 'If Contains True Value'], ['freeEmailFalse', 'If Not Contains False Value']]],
  ['Name Match', [['nameMatchTrue', 'If Billing Name = Shipping Name'], ['nameMatchFalse', 'If Billing Name <> Shipping Name']]],
  ['Address Match', [['addressMatchTrue', 'If Billing Address = Shipping Address'], ['addressMatchFalse', 'If Billing Address <> Shipping Address']]],
  ['Gift Certificate', [['giftCardUsedOrderAmountGreaterThen30', 'If Gift Card is Used and order amount greater then $30'], ['giftCardUsedOrderAmountLessThen30', 'If Gift Card is Used and order amount less then $30']]],
  ['Known Fraud Address', [['addressInTable', 'Value if address in the table'], ['addressNotInTable', 'Value if address not in the table']]],
  ['Threshold', [['fraudThreshold', 'Sum Value Over "X" = Fraud']]],
];
const blankAddress = { id: 0, orderId: '', houseNumber: '', leadingDirectional: '', streetName: '', streetSuffix: '', zip: '', plusFour: '' };
const digits = (v) => /^\d+$/.test(String(v));
const noNulls = (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v == null ? '' : v]));

export default function FraudCheck() {
  const live = useWriteLive('ws8_fraud_check');
  const [config, setConfig] = useState(null);
  const [shipTypes, setShipTypes] = useState([]);
  const [configError, setConfigError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [f, setF] = useState({ orderId: '', houseNumber: '', streetName: '', zip: '' });
  const [edit, setEdit] = useState(null);
  const [lookup, setLookup] = useState(null);
  const addresses = useRows(() => fetchFraudAddresses(f), [f]);
  const addressSave = useSave(addresses.reload);
  const configSave = useSave(() => {});

  useEffect(() => {
    Promise.all([fetchFraudConfig(), fetchShippingTypes()])
      .then(([rows, types]) => {
        const values = {};
        rows.forEach((r) => { values[r.key] = r.value; });
        // A shipping type with no row yet shows 0 and is added on save, as in the admin portal.
        types.forEach((t) => { if (values[t.name] === undefined) values[t.name] = '0'; });
        setShipTypes(types.map((t) => t.name));
        setConfig(values);
      })
      .catch((e) => setConfigError(e.message));
  }, []);

  const set = (key, value) => { setSaved(false); setConfig({ ...config, [key]: value }); };
  const box = (key, label) => (
    <Field key={key} label={label}><input className="form-input" disabled={!live} value={config[key] == null ? '' : config[key]} onChange={(e) => set(key, e.target.value)} /></Field>
  );
  const saveConfig = async () => {
    const keys = [...SECTIONS.flatMap(([, list]) => list.map(([k]) => k)), ...shipTypes];
    if (keys.some((k) => config[k] == null || String(config[k]) === '')) return configSave.setError('Please fill all required fields!');
    setSaved(!!(await configSave.save('fraud_config_save', { pairs: keys.map((key) => ({ key, value: config[key] })) })));
    return null;
  };
  const fillFromOrder = async () => {
    if (!edit.orderId) return setLookup('Order ID is required to fetch the address!');
    const r = (await fetchOrderShipAddress(edit.orderId))[0];
    if (!r || !r.address || !r.zip) return setLookup('Order shipping address not found!');
    const m = r.address.match(/^(\d+)\s*(.*)/);
    const [zip, plusFour] = r.zip.length > 5 ? r.zip.split('-') : [r.zip, ''];
    setLookup(null);
    setEdit({ ...edit, houseNumber: m ? m[1] : '', streetName: m ? m[2] : r.address, leadingDirectional: '', streetSuffix: '', zip, plusFour: plusFour || '' });
    return null;
  };
  const submit = async () => {
    const e = edit;
    if (!digits(e.orderId) || !digits(e.houseNumber) || !e.streetName.trim() || !digits(e.zip)) return addressSave.setError('Form is invalid');
    if (await addressSave.save('fraud_address_save', e)) setEdit(null);
    return null;
  };
  const text = (key, label) => <Field label={label}><input className="form-input" value={edit[key]} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} /></Field>;

  return (
    <div>
      <PageTitle>Fraud Check</PageTitle>
      <WriteOffNote live={live} what="Weight changes and address add and edit" />
      {configError && <div className="staff-msg staff-msg-error">Error: {configError}</div>}
      {!config && !configError && <div className="staff-msg">Loading...</div>}
      {config && (
        <div className="card">
          <div className="card-header">Score weights</div>
          <div className="modal-body">
            {SECTIONS.slice(0, 3).map(([title, list]) => <div key={title}><h3 className="staff-subhead">{title}</h3><div className="staff-grid">{list.map(([k, l]) => box(k, l))}</div></div>)}
            <h3 className="staff-subhead">Shipping Type</h3>
            <div className="staff-grid">{shipTypes.map((name) => box(name, name))}</div>
            {SECTIONS.slice(3).map(([title, list]) => <div key={title}><h3 className="staff-subhead">{title}</h3><div className="staff-grid">{list.map(([k, l]) => box(k, l))}</div></div>)}
            {configSave.error && <div className="staff-msg staff-msg-error">{configSave.error}</div>}
            {saved && !configSave.error && <div className="staff-msg">Saved</div>}
            <button type="button" className="btn btn-primary" disabled={!live || configSave.saving} onClick={saveConfig}>Save</button>
          </div>
        </div>
      )}
      <div className="staff-section">
        <div className="port-filters">
          <input className="form-input" style={{ width: 'auto' }} placeholder="Order Id" aria-label="Order Id" value={f.orderId} onChange={(e) => setF({ ...f, orderId: e.target.value })} />
          <input className="form-input" style={{ width: 'auto' }} placeholder="House Number" aria-label="House Number" value={f.houseNumber} onChange={(e) => setF({ ...f, houseNumber: e.target.value })} />
          <input className="form-input" style={{ width: 'auto' }} placeholder="Street Name" aria-label="Street Name" value={f.streetName} onChange={(e) => setF({ ...f, streetName: e.target.value })} />
          <input className="form-input" style={{ width: 'auto' }} placeholder="Zip" aria-label="Zip" value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })} />
          <button type="button" className="btn btn-primary" disabled={!live} onClick={() => { addressSave.setError(null); setLookup(null); setEdit(blankAddress); }}>Add Address</button>
        </div>
        <ListCard title="Known fraud addresses" rows={addresses.rows} error={addresses.error} empty="No addresses.">
          <table className="trans-table">
            <thead><tr><th>OrderId</th><th>House Number</th><th>Leading Directional</th><th>Street Name</th><th>Street Suffix</th><th>Zip</th><th>Plus Four</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {(addresses.rows || []).map((r) => (
                <tr key={r.id}>
                  <td>{r.bcOrderId || r.orderId}</td>
                  <td>{r.houseNumber}</td>
                  <td>{r.leadingDirectional}</td>
                  <td>{r.streetName}</td>
                  <td>{r.streetSuffix}</td>
                  <td>{r.zip}</td>
                  <td>{r.plusFour}</td>
                  <td className="staff-actions"><button type="button" className="btn btn-ghost" disabled={!live || addressSave.saving} onClick={() => { addressSave.setError(null); setLookup(null); setEdit(noNulls(r)); }}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </ListCard>
      </div>
      {edit && (
        <EditDialog title={edit.id ? 'Edit Address' : 'Add Address'} live={live} saving={addressSave.saving} error={addressSave.error} onClose={() => setEdit(null)} onSave={submit}>
          {text('orderId', 'Order Id')}
          <button type="button" className="btn btn-outline" onClick={fillFromOrder}>Fill in from the order</button>
          {lookup && <div className="staff-hint">{lookup}</div>}
          <div className="staff-grid">
            {text('houseNumber', 'House Number')}
            {text('leadingDirectional', 'Leading Directional')}
            {text('streetName', 'Street Name')}
            {text('streetSuffix', 'Street Suffix')}
            {text('zip', 'Zip')}
            {text('plusFour', 'Plus Four')}
          </div>
        </EditDialog>
      )}
    </div>
  );
}
